<script setup lang="ts">
import {
    computed, ref, watch, nextTick,
} from 'vue';
import { useTranslation } from '@/composables';
import BChip from './BChip.vue';
import BAutocomplete from './BAutocomplete.vue';
import BDateTimeField from './BDateTimeField.vue';
import BListItem from './BListItem.vue';
import BListItemSubtitle from './BListItemSubtitle.vue';
import {
    BDivider, BListItemTitle, BIcon,
} from '.';
import type { ConditionFieldType, ConditionOperatorType } from '@/types/HookConditionTypes';

type ChipData = string | string[] | ConditionFieldType | ConditionOperatorType | Record<string, unknown>;

type PropsType = {
    type: 'field' | 'operator' | 'value';
    mode?: 'chip' | 'input';
    data?: ChipData;
    displayText?: string;
    placeholder?: string;
    size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large';
    location?: 'top' | 'bottom' | 'start' | 'end' | 'center';
    showClose?: boolean;
    items?: ChipData[];
    itemTitle?: string;
    itemValue?: string;
    allowTyping?: boolean;
    autofocus?: boolean;
    autoOpenMenu?: boolean;
    loading?: boolean;
    autoCloseMenu?: boolean;
    isDateField?: boolean;
    dateInputType?: 'datetime-local' | 'date' | 'time';
    multiple?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    mode: 'chip',
    data: undefined,
    size: 'default',
    location: 'bottom',
    showClose: false,
    items: () => [],
    itemTitle: 'label',
    itemValue: 'name',
    allowTyping: true,
    autofocus: false,
    autoOpenMenu: false,
    loading: false,
    autoCloseMenu: false,
    isDateField: false,
    dateInputType: 'date',
    multiple: false,
});

const emit = defineEmits<{
    remove: [];
    select: [value: ChipData];
    click: [];
    'update:menu': [value: boolean];
    search: [query: string];
}>();

const { t } = useTranslation();

defineOptions({ inheritAttrs: false });

const isOperatorData = (data: ChipData | undefined): data is ConditionOperatorType => {
    return data !== undefined && typeof data === 'object' && data !== null && 'symbol' in data && 'name' in data;
};

const menuOpen = ref(false);

watch(
    [() => props.autoOpenMenu, () => props.mode],
    async ([shouldAutoOpen, currentMode]) => {
        if (shouldAutoOpen && currentMode === 'input') {
            await nextTick();
            menuOpen.value = true;
        }
    },
    { immediate: true },
);

watch(
    () => props.autoCloseMenu,
    newValue => {
        if (newValue && menuOpen.value) {
            menuOpen.value = false;
        }
    },
);

const chipClass = computed(() => {
    const classes = [`editable-chip--${props.type}`, 'editable-chip'];

    return classes;
});

const operatorSymbol = computed(() => {
    if (props.type === 'operator' && props.mode === 'chip' && isOperatorData(props.data)) {
        return props.data.symbol;
    }

    return null;
});

const handleRemove = (event: Event) => {
    event.stopPropagation();
    emit('remove');
};

const handleChipClick = async () => {
    emit('click');

    if (!props.autoOpenMenu) {
        await nextTick();
        menuOpen.value = true;
    }
};

const handleAutocompleteSelect = (value: unknown) => {
    if (props.mode !== 'input') {
        return;
    }

    // Multi-select accumulates picks: emit the full array and keep the menu open.
    if (props.multiple) {
        emit('select', (value ?? []) as ChipData);

        return;
    }

    if (value !== null && value !== undefined) {
        emit('select', value as ChipData);
        menuOpen.value = false;
    }
};

const handleEnterKey = (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    if (inputValue && inputValue.trim()) {
        event.preventDefault();
        event.stopPropagation();

        // In multi-select mode picks come from the list (as an array), not from
        // free-typed Enter, so ignore the custom-value shortcut here.
        if (props.type === 'value' && !props.multiple) {
            emit('select', inputValue);
        }

        if (!props.multiple) {
            menuOpen.value = false;
        }
    }
};

// Multi-select: confirm the current selection on Enter. Handled in the capture
// phase so it runs before the autocomplete's own Enter handler (which would
// otherwise toggle the highlighted item and keep the menu open). The parent
// commits the accumulated picks when the menu closes.
const handleEnterCapture = (event: KeyboardEvent) => {
    if (!props.multiple) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    menuOpen.value = false;
    emit('update:menu', false);
};

const handleBackspaceKey = (event: KeyboardEvent) => {
    // Stop propagation when in input mode to prevent condition deletion
    if (props.mode === 'input') {
        event.stopPropagation();
    }
};

const handleMenuToggle = (value: boolean) => {
    menuOpen.value = value;
    emit('update:menu', value);
};

const handleDateFieldChange = (value: string | null) => {
    if (value) {
        emit('select', value);
        emit('update:menu', false);
    }
};

const handleSearch = (query: string) => {
    emit('search', query);
};
</script>

<template>
    <div
        v-if="props.mode === 'input'"
        class="editable-chip-input"
        @keydown.enter.capture="handleEnterCapture"
    >
        <BDateTimeField
            v-if="isDateField && type === 'value'"
            :model-value="String(data || '')"
            :placeholder="placeholder"
            :auto-open-picker="autofocus"
            density="compact"
            variant="plain"
            :input-type="dateInputType"
            :autofocus="autofocus"
            :hide-details="true"
            class="chip-date-field"
            @change="handleDateFieldChange"
            @update:focused="handleMenuToggle"
            @keydown.delete="handleBackspaceKey"
        />
        <BAutocomplete
            v-else
            :model-value="data"
            :placeholder="placeholder"
            :items="items"
            :item-title="itemTitle"
            :item-value="itemValue"
            :hide-details="true"
            density="compact"
            variant="plain"
            :loading="loading"
            :multiple="multiple"
            :chips="multiple"
            :closable-chips="multiple"
            :return-object="props.type !== 'value'"
            :clearable="false"
            :list-props="{
                density: 'compact',
            }"
            :autofocus="autofocus"
            :menu="menuOpen"
            class="chip-autocomplete"
            @update:model-value="handleAutocompleteSelect"
            @update:menu="handleMenuToggle"
            @keydown.enter="handleEnterKey"
            @keydown.delete="handleBackspaceKey"
            @update:search="handleSearch"
        >
            <template
                v-if="$slots.item"
                #item="{ props: itemProps, item }"
            >
                <slot
                    name="item"
                    :props="itemProps"
                    :item="item"
                />
            </template>

            <template #no-data>
                <template v-if="!loading">
                    <template v-if="type === 'value'">
                        <BListItem>
                            <BListItemTitle>
                                {{ t('hooks.conditions.customValueHint') }}
                            </BListItemTitle>
                        </BListItem>
                        <BDivider />
                        <BListItem>
                            <BListItemTitle>
                                {{ t('hooks.conditions.noSuggestions') }}
                            </BListItemTitle>
                        </BListItem>
                    </template>
                    <template v-else>
                        <BListItem>
                            <BListItemTitle>
                                {{ t(type === 'field' ? 'hooks.conditions.noFields' : 'hooks.conditions.noOperators') }}
                            </BListItemTitle>
                        </BListItem>
                    </template>
                </template>

                <template v-else>
                    <BListItem>
                        <BListItemSubtitle>
                            {{ t('common.loading') }}
                        </BListItemSubtitle>
                    </BListItem>
                </template>
            </template>
        </BAutocomplete>
    </div>

    <BChip
        v-else
        variant="flat"
        :size="size"
        :class="chipClass"
        :closable="showClose"
        @click.prevent.stop="handleChipClick"
        @click:close="handleRemove"
    >
        <template v-if="operatorSymbol">
            <BIcon
                size="15"
                class="mr-1"
            >{{ operatorSymbol }}</BIcon
            >
        </template>
        <span v-else>{{ displayText }}</span>
    </BChip>
</template>

<style scoped>
.v-chip {
    border-radius: 1px;
    min-width: fit-content;
}

.editable-chip {
    --v-theme-overlay-multiplier: var(--v-theme-grey-600-overlay-multiplier);
    background-color: rgb(var(--v-theme-grey-600)) !important;
    color: rgb(var(--v-theme-on-grey-600)) !important;
}

.is-filter-container .editable-chip {
    --v-theme-overlay-multiplier: var(--v-theme-grey-300-overlay-multiplier);
    background-color: rgb(var(--v-theme-grey-200)) !important;
    color: rgb(var(--v-theme-on-grey-300)) !important;
}

.editable-chip--editable {
    cursor: pointer;
    transition: all 0.2s ease;
}

.editable-chip--editable:hover {
    background-color: rgb(var(--v-theme-grey-lighten-2));
}

.editable-chip-input {
    display: inline-block;
    min-width: 120px;
    margin-right: 100px;
    position: relative;
}

:deep(.chip-autocomplete .v-field__input) {
    padding: 0;
    min-height: 24px;
}

:deep(.chip-autocomplete .v-field) {
    background: transparent;
}

:deep(.chip-autocomplete .v-field__append-inner) {
    display: none;
}

:deep(.chip-date-field .v-field__input) {
    padding: 0;
    min-height: 24px;
}

:deep(.chip-date-field .v-field) {
    background: transparent;
}

:deep(.chip-date-field .v-field__append-inner) {
    display: none;
}

:deep(.chip-date-field input::-webkit-calendar-picker-indicator) {
    display: none;
    -webkit-appearance: none;
}

:deep(.chip-date-field input[type='date']),
:deep(.chip-date-field input[type='datetime-local']) {
    cursor: pointer;
}

:deep(.chip-date-field input[type='date']::-webkit-inner-spin-button),
:deep(.chip-date-field input[type='datetime-local']::-webkit-inner-spin-button) {
    display: none;
    -webkit-appearance: none;
}
</style>
